import React, { useCallback } from 'react';
import { Calculator, Car, Home, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета налога
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Обработчик переключения типа объекта налогообложения
  const handleObjectTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      taxObjectType: type
    }));
  };
  
  // Обработчик изменения стоимости автомобиля
  const handleCarCostChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      carCost: value
    }));
  };
  
  // Обработчик изменения мощности автомобиля
  const handleCarPowerChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      carPower: value
    }));
  };
  
  // Обработчик изменения стоимости недвижимости
  const handlePropertyCostChange = (e) => {
    const value = e.target.value;
    const numValue = parseFloat(value);
    
    setFormData(prev => ({
      ...prev,
      propertyCost: value,
      // Обычно кадастровая стоимость составляет около 80% от рыночной
      cadastralValue: !isNaN(numValue) ? Math.round(numValue * 0.8).toString() : prev.cadastralValue
    }));
  };
  
  // Обработчик изменения кадастровой стоимости
  const handleCadastralValueChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      cadastralValue: value
    }));
  };
  
  // Обработчик изменения типа автомобиля
  const handleCarTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      carType: type
    }));
  };
  
  // Обработчик изменения типа недвижимости
  const handlePropertyTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      propertyType: type
    }));
  };
  
  // Обработчик переключения льготы для электромобилей
  const handleElectricCarToggle = () => {
    setFormData(prev => ({
      ...prev,
      isElectric: !prev.isElectric
    }));
  };
  
  // Обработчик переключения единственного жилья
  const handleOnlyPropertyToggle = () => {
    setFormData(prev => ({
      ...prev,
      isOnlyProperty: !prev.isOnlyProperty
    }));
  };
  
  // Функция расчета
  const calculateTax = useCallback(() => {
    // Здесь должна быть вызвана функция основного расчета из родительского компонента
    document.querySelector('button[type="submit"]')?.click();
  }, []);
  
  // Проверка наличия ошибок для отключения кнопки
  const hasErrors = Object.keys(errors).length > 0;
  
  // Формирование меток для ползунков
  const carCostLabel = formatCurrency(parseFloat(formData.carCost) || 0, 0);
  const carPowerLabel = `${formData.carPower} л.с.`;
  const propertyCostLabel = formatCurrency(parseFloat(formData.propertyCost) || 0, 0);
  const cadastralValueLabel = formatCurrency(parseFloat(formData.cadastralValue) || 0, 0);
  
  const showCarParams = formData.taxObjectType === 'car';
  const showPropertyParams = formData.taxObjectType === 'property';
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Выбор типа объекта налогообложения */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Тип объекта налогообложения
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleObjectTypeChange('car')}
              className={`flex items-center justify-center p-3 rounded-lg border transition-colors ${
                showCarParams
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/70'
              }`}
              aria-pressed={showCarParams}
            >
              <Car className="w-5 h-5 mr-2" />
              <span>Автомобиль</span>
            </button>
            <button
              type="button"
              onClick={() => handleObjectTypeChange('property')}
              className={`flex items-center justify-center p-3 rounded-lg border transition-colors ${
                showPropertyParams
                  ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/70'
              }`}
              aria-pressed={showPropertyParams}
            >
              <Home className="w-5 h-5 mr-2" />
              <span>Недвижимость</span>
            </button>
          </div>
        </div>
        
        {/* Параметры для автомобилей */}
        {showCarParams && (
          <>
            {/* Стоимость автомобиля */}
            <div>
              <div className="flex justify-between items-center mb-1 text-sm">
                <label htmlFor="carCostSlider" className="text-gray-600 dark:text-gray-400">Стоимость автомобиля</label>
                <span className="font-medium text-gray-900 dark:text-white">{carCostLabel}</span>
              </div>
              <input
                type="range"
                id="carCostSlider"
                min="300000"
                max="20000000"
                step="100000"
                value={formData.carCost}
                onChange={handleCarCostChange}
                aria-label="Стоимость автомобиля"
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              
              {/* Кнопки с предопределенными стоимостями */}
              <div className="grid grid-cols-4 gap-1 mt-2">
                {['1000000', '3000000', '5000000', '10000000'].map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, carCost: value }))}
                    className={`px-2 py-1 text-xs rounded border transition-colors
                      ${formData.carCost === value 
                        ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                  >
                    {value === '1000000' ? '1 млн' : 
                     value === '3000000' ? '3 млн' : 
                     value === '5000000' ? '5 млн' : 
                     value === '10000000' ? '10 млн' : value}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Мощность автомобиля */}
            <div>
              <div className="flex justify-between items-center mb-1 text-sm">
                <label htmlFor="carPowerSlider" className="text-gray-600 dark:text-gray-400">Мощность двигателя</label>
                <span className="font-medium text-gray-900 dark:text-white">{carPowerLabel}</span>
              </div>
              <input
                type="range"
                id="carPowerSlider"
                min="50"
                max="500"
                step="10"
                value={formData.carPower}
                onChange={handleCarPowerChange}
                aria-label="Мощность двигателя (л.с.)"
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              
              {/* Кнопки с предопределенными значениями мощности */}
              <div className="grid grid-cols-4 gap-1 mt-2">
                {['100', '150', '200', '300'].map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, carPower: value }))}
                    className={`px-2 py-1 text-xs rounded border transition-colors
                      ${formData.carPower === value 
                        ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                  >
                    {value} л.с.
                  </button>
                ))}
              </div>
            </div>
            
            {/* Тип автомобиля */}
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Тип автомобиля
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleCarTypeChange('standard')}
                  className={`p-2 text-sm rounded-lg border transition-colors
                    ${formData.carType === 'standard' 
                      ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  aria-pressed={formData.carType === 'standard'}
                >
                  <div className="font-medium">Стандартный</div>
                  <div className="text-xs opacity-75">Обычный коэффициент</div>
                </button>
                <button
                  type="button"
                  onClick={() => handleCarTypeChange('luxury')}
                  className={`p-2 text-sm rounded-lg border transition-colors
                    ${formData.carType === 'luxury' 
                      ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  aria-pressed={formData.carType === 'luxury'}
                >
                  <div className="font-medium">Люксовый</div>
                  <div className="text-xs opacity-75">Повыш. коэффициент</div>
                </button>
              </div>
            </div>
            
            {/* Электромобиль */}
            <div>
              <button
                type="button"
                onClick={handleElectricCarToggle}
                className={`w-full p-3 text-sm rounded-lg border transition-colors flex items-center justify-between
                  ${formData.isElectric 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                aria-pressed={formData.isElectric}
              >
                <span className="font-medium">Электромобиль</span>
                <div className={`w-10 h-5 rounded-full relative ${formData.isElectric ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                  <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all ${formData.isElectric ? 'left-5' : 'left-0.5'}`}></div>
                </div>
              </button>
            </div>
          </>
        )}
        
        {/* Параметры для недвижимости */}
        {showPropertyParams && (
          <>
            {/* Рыночная стоимость недвижимости */}
            <div>
              <div className="flex justify-between items-center mb-1 text-sm">
                <label htmlFor="propertyCostSlider" className="text-gray-600 dark:text-gray-400">Рыночная стоимость</label>
                <span className="font-medium text-gray-900 dark:text-white">{propertyCostLabel}</span>
              </div>
              <input
                type="range"
                id="propertyCostSlider"
                min="1000000"
                max="100000000"
                step="1000000"
                value={formData.propertyCost}
                onChange={handlePropertyCostChange}
                aria-label="Рыночная стоимость недвижимости"
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
              
              {/* Кнопки с предопределенными стоимостями */}
              <div className="grid grid-cols-4 gap-1 mt-2">
                {['5000000', '10000000', '30000000', '50000000'].map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      propertyCost: value,
                      cadastralValue: Math.round(parseFloat(value) * 0.8).toString()
                    }))}
                    className={`px-2 py-1 text-xs rounded border transition-colors
                      ${formData.propertyCost === value 
                        ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                  >
                    {value === '5000000' ? '5 млн' : 
                     value === '10000000' ? '10 млн' : 
                     value === '30000000' ? '30 млн' : 
                     value === '50000000' ? '50 млн' : value}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Кадастровая стоимость */}
            <div>
              <div className="flex justify-between items-center mb-1 text-sm">
                <label htmlFor="cadastralValueSlider" className="text-gray-600 dark:text-gray-400">Кадастровая стоимость</label>
                <span className="font-medium text-gray-900 dark:text-white">{cadastralValueLabel}</span>
              </div>
              <input
                type="range"
                id="cadastralValueSlider"
                min="800000"
                max="80000000"
                step="800000"
                value={formData.cadastralValue}
                onChange={handleCadastralValueChange}
                aria-label="Кадастровая стоимость недвижимости"
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
            </div>
            
            {/* Тип недвижимости */}
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Тип недвижимости
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handlePropertyTypeChange('apartment')}
                  className={`p-2 text-sm rounded-lg border transition-colors
                    ${formData.propertyType === 'apartment' 
                      ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  aria-pressed={formData.propertyType === 'apartment'}
                >
                  <div className="font-medium">Квартира</div>
                  <div className="text-xs opacity-75">Жилое помещение</div>
                </button>
                <button
                  type="button"
                  onClick={() => handlePropertyTypeChange('house')}
                  className={`p-2 text-sm rounded-lg border transition-colors
                    ${formData.propertyType === 'house' 
                      ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                  aria-pressed={formData.propertyType === 'house'}
                >
                  <div className="font-medium">Дом</div>
                  <div className="text-xs opacity-75">Жилой дом</div>
                </button>
              </div>
            </div>
            
            {/* Единственное жилье */}
            <div>
              <button
                type="button"
                onClick={handleOnlyPropertyToggle}
                className={`w-full p-3 text-sm rounded-lg border transition-colors flex items-center justify-between
                  ${formData.isOnlyProperty 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                aria-pressed={formData.isOnlyProperty}
              >
                <span className="font-medium">Единственное жилье</span>
                <div className={`w-10 h-5 rounded-full relative ${formData.isOnlyProperty ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                  <div className={`absolute w-4 h-4 bg-white rounded-full top-0.5 transition-all ${formData.isOnlyProperty ? 'left-5' : 'left-0.5'}`}></div>
                </div>
              </button>
            </div>
          </>
        )}
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTax}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || hasErrors}
          isLoading={isCalculating}
        >
          Рассчитать налог
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;